import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useAppState } from "../../hooks/useAppState";
import { getVanillaEl } from "../../domBridge";
import { appState } from "../../../state";
import { notifyStateChange } from "../../../stateBus";
import { requestLock } from "../../../lockBus";
import { showToast } from "../../../toast";
import { confirmDialog } from "../../../dialogs";
import { pickAlbum } from "../../../albumPicker";
import { loadAlbums } from "../../../albumsApi";
import { deleteMediaApi, loadGalleryData } from "../../../galleryApi";
import { apiFetch } from "../../../api";

export function toggleSelectMode(force?: boolean): void {
  const next = force !== undefined ? force : !appState.selectMode;
  appState.selectMode = next;
  if (!next) appState.selectedFilenames = new Set();
  notifyStateChange();
}

async function handleBulkDelete(): Promise<void> {
  const filenames = Array.from(appState.selectedFilenames);
  if (!filenames.length) return;

  const ok = await confirmDialog({
    title: "Delete Media",
    message: `Delete ${filenames.length} item${filenames.length === 1 ? "" : "s"}? This cannot be undone.`,
    confirmLabel: "Delete",
  });
  if (!ok) return;

  let failed = 0;
  for (const filename of filenames) {
    const result = await deleteMediaApi(filename);
    if (!result.ok) {
      if (result.error === "Unauthorized") return; // deleteMediaApi already called requestLock
      failed++;
    }
  }

  toggleSelectMode(false);
  await loadAlbums();
  await loadGalleryData();

  if (failed) showToast(`Deleted ${filenames.length - failed} item(s), ${failed} failed`, "error");
  else showToast(`Deleted ${filenames.length} item(s)`, "success");
}

async function handleBulkMove(): Promise<void> {
  const filenames = Array.from(appState.selectedFilenames);
  if (!filenames.length) return;

  const chosen = await pickAlbum("Move to Album", { includeAll: false });
  if (!chosen) return;

  let failed = 0;
  for (const filename of filenames) {
    try {
      const res = await apiFetch(`/api/albums/${chosen.id}/media`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ filename }),
      });
      if (res.status === 401) {
        requestLock("unauthorized");
        return;
      }
      if (!res.ok) failed++;
    } catch {
      failed++;
    }
  }

  toggleSelectMode(false);
  await loadAlbums();

  if (failed) showToast(`Added ${filenames.length - failed} item(s) to "${chosen.name}", ${failed} failed`, "error");
  else showToast(`Added ${filenames.length} item(s) to "${chosen.name}"`, "success");
}

function SelectModeBarBody() {
  const selectMode = useAppState((s) => s.selectMode);
  const selectedCount = useAppState((s) => s.selectedFilenames.size);

  // The trigger itself is now AlbumHeroHeader.tsx's overflow-menu "Select
  // media" item (calls the exported toggleSelectMode(true) directly) -
  // this only owns reacting to selectMode once it's on: hiding the bottom
  // tab bar while the bulk action bar takes its place.
  useEffect(() => {
    getVanillaEl<HTMLElement>("appTabBar")?.classList.toggle("bulk-hidden", selectMode);
  }, [selectMode]);

  const container = getVanillaEl<HTMLDivElement>("bulkActionBar");

  useEffect(() => {
    container?.classList.toggle("d-none", !selectMode);
  }, [container, selectMode]);

  if (!container) return null;

  return createPortal(
    <>
      <button type="button" className="bulk-action-cancel" aria-label="Cancel selection" onClick={() => toggleSelectMode(false)}>
        <i className="bi bi-x-lg" aria-hidden="true" />
      </button>
      <span className="bulk-action-count">{selectedCount} selected</span>
      <div className="bulk-action-buttons">
        <button type="button" className="btn btn-outline-pink btn-sm" disabled={!selectedCount} onClick={handleBulkMove}>
          <i className="bi bi-folder-symlink me-1" aria-hidden="true" />
          Move
        </button>
        <button type="button" className="btn btn-danger btn-sm" disabled={!selectedCount} onClick={handleBulkDelete}>
          <i className="bi bi-trash3 me-1" aria-hidden="true" />
          Delete
        </button>
      </div>
    </>,
    container,
  );
}

export function SelectModeBar() {
  return <SelectModeBarBody />;
}
